import {
  forwardRef,
  type MouseEvent,
  memo,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useIsomorphicLayoutEffect } from 'react-use';

import { cn } from '@air/utils-tailwind';

import { type Skill } from '../../skills';

/**
 * Returning true from onKeyDown means the key was handled (same contract as SkillSuggestionPopup /
 * TipTap Suggestion). Escape returns false so the parent can dismiss while the composer stays focused.
 */
export type SkillSuggestionPillRef = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type SkillSuggestionPillProps = {
  items: Skill[];
  onSelect: (skill: Skill) => void;
  /** Composer frame used to position the portaled pill above the input (avoids overflow clipping). */
  anchorRef: RefObject<HTMLElement | null>;
  className?: string; // NOSONAR -- used by the inner component; S6774 misses memo(forwardRef)
  /** Sizes to its rows, capped at the anchor's width, instead of spanning the anchor. */
  fitContent?: boolean; // NOSONAR -- used by the inner component; S6774 misses memo(forwardRef)
  /** Opens under the anchor instead of above it. */
  placement?: 'above' | 'below'; // NOSONAR -- used by the inner component; S6774 misses memo(forwardRef)
  /** Optional dismiss for Escape when the listbox itself has focus. */
  onDismiss?: () => void;
  /** Dismiss when pointerdown is outside the composer + pill (does not suppress the typed query). */
  onPointerDownOutside?: () => void;
};

const PILL_GAP_PX = 8;

type PillPosition = {
  left: number;
  width: number;
  top: number;
};

export const SkillSuggestionPill = memo(
  forwardRef<SkillSuggestionPillRef, SkillSuggestionPillProps>(
    (
      {
        anchorRef,
        className,
        fitContent = false,
        items,
        onDismiss,
        onPointerDownOutside,
        onSelect,
        placement = 'above',
      },
      ref,
    ) => {
      const [selectedIndex, setSelectedIndex] = useState(0);
      const [position, setPosition] = useState<PillPosition | null>(null);
      const itemsVersion = useMemo(() => items.map((item) => item.slashCommand).join('\0'), [items]);
      const isSingle = items.length === 1;
      const activeDescendantId = items[selectedIndex] ? `skill-suggestion-pill-option-${selectedIndex}` : undefined;
      const selected = items[selectedIndex];

      // biome-ignore lint/correctness/useExhaustiveDependencies: reset highlight when the filtered list identity changes
      useEffect(() => {
        setSelectedIndex(0);
      }, [itemsVersion]);

      useIsomorphicLayoutEffect(() => {
        const updatePosition = () => {
          const anchor = anchorRef.current;

          if (!anchor) {
            setPosition(null);
            return;
          }

          const rect = anchor.getBoundingClientRect();
          setPosition({
            left: rect.left,
            top: placement === 'below' ? rect.bottom + PILL_GAP_PX : rect.top - PILL_GAP_PX,
            width: rect.width,
          });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
          window.removeEventListener('scroll', updatePosition, true);
          window.removeEventListener('resize', updatePosition);
        };
      }, [anchorRef, itemsVersion, placement]);

      const selectItem = useCallback(
        (index: number) => {
          const item = items[index];

          if (item) {
            onSelect(item);
          }
        },
        [items, onSelect],
      );

      const handleListKeyDown = useCallback(
        (event: KeyboardEvent | ReactKeyboardEvent) => {
          if (items.length === 0) {
            return false;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % items.length);

            return true;
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);

            return true;
          }

          if (event.key === 'Enter') {
            event.preventDefault();
            selectItem(selectedIndex);

            return true;
          }

          // Escape: return false so the parent can dismiss (composer still focused).
          return false;
        },
        [items.length, selectItem, selectedIndex],
      );

      useImperativeHandle(
        ref,
        () => ({
          onKeyDown: (event: KeyboardEvent) => handleListKeyDown(event),
        }),
        [handleListKeyDown],
      );

      const handleListboxKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Escape') {
            onDismiss?.();

            return;
          }

          handleListKeyDown(event);
        },
        [handleListKeyDown, onDismiss],
      );

      const handleOptionClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
          const index = Number(event.currentTarget.dataset.index);

          if (!Number.isNaN(index)) {
            selectItem(index);
          }
        },
        [selectItem],
      );

      const handleOptionMouseEnter = useCallback((event: MouseEvent<HTMLDivElement>) => {
        const index = Number(event.currentTarget.dataset.index);

        if (!Number.isNaN(index)) {
          setSelectedIndex(index);
        }
      }, []);

      // Keep composer focus so blur does not tear down the pill before click selects a skill.
      const handleOptionMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
      }, []);

      const handleOptionKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const index = Number(event.currentTarget.dataset.index);

            if (!Number.isNaN(index)) {
              selectItem(index);
            }
          }
        },
        [selectItem],
      );

      // Mirror Radix onPointerDownOutside for the portaled pill (not a Popover).
      useEffect(() => {
        if (!onPointerDownOutside) {
          return;
        }

        const handlePointerDown = (event: PointerEvent) => {
          const target = event.target;

          if (!(target instanceof Node)) {
            return;
          }

          const pill = document.querySelector('[data-skill-word-suggestion-pill]');
          const anchor = anchorRef.current;

          if (pill?.contains(target) || anchor?.contains(target)) {
            return;
          }

          onPointerDownOutside();
        };

        document.addEventListener('pointerdown', handlePointerDown, true);

        return () => {
          document.removeEventListener('pointerdown', handlePointerDown, true);
        };
      }, [anchorRef, onPointerDownOutside]);

      if (items.length === 0 || !position) {
        return null;
      }

      return createPortal(
        <div
          className={cn(
            'fixed z-portal rounded-16 ads:bg-raised bg-gray-1 shadow-popover ads:ring-ads-gray-5 ring-1 ring-gray-5',
            isSingle ? 'p-3' : 'p-1.5',
            placement === 'above' && '-translate-y-full',
            fitContent && 'w-max',
            className,
          )}
          data-skill-word-suggestion-pill=""
          data-testid="SKILL_WORD_SUGGESTION_PILL"
          style={{
            left: position.left,
            maxWidth: fitContent ? position.width : undefined,
            top: position.top,
            width: fitContent ? undefined : position.width,
          }}
        >
          <div
            aria-activedescendant={activeDescendantId}
            aria-label="Skill suggestions"
            className={cn('flex flex-col outline-none', !isSingle && 'gap-0.5')}
            onKeyDown={handleListboxKeyDown}
            role="listbox"
            tabIndex={-1}
          >
            {items.map((item, index) => {
              const isSelected = index === selectedIndex;

              return (
                <div
                  aria-label={`Use skill /${item.slashCommand}`}
                  aria-selected={isSelected}
                  className={cn(
                    'flex w-full cursor-pointer flex-col gap-0.5 overflow-hidden text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-9',
                    isSingle
                      ? 'rounded-8 px-0 py-0'
                      : 'rounded-4 px-2 py-1.5 hover:ads:bg-hover-subtle hover:bg-gray-4 active:ads:bg-ads-gray-5 active:bg-gray-5',
                    !isSingle && isSelected && 'ads:bg-hover-subtle bg-gray-4',
                  )}
                  data-index={index}
                  id={`skill-suggestion-pill-option-${index}`}
                  key={item.slashCommand}
                  onClick={handleOptionClick}
                  onKeyDown={handleOptionKeyDown}
                  onMouseDown={handleOptionMouseDown}
                  onMouseEnter={handleOptionMouseEnter}
                  role="option"
                  tabIndex={-1}
                >
                  <div className="truncate font-medium font-mono ads:text-primary text-14 text-gray-12">
                    /{item.slashCommand}
                  </div>
                  {item.description ? (
                    <div className="truncate font-normal ads:text-metadata text-12 text-gray-10">
                      {item.description}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div aria-live="polite" className="sr-only">
            {selected
              ? `${selected.name}${selected.description ? `, ${selected.description}` : ''}, ${selectedIndex + 1} of ${items.length}`
              : null}
          </div>
        </div>,
        document.body,
      );
    },
  ),
);

SkillSuggestionPill.displayName = 'SkillSuggestionPill';
